import { styleConstants } from '@paywithextend/components'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import React, { useCallback, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  selectCreditCardIdFromRoute,
  selectPatchingCreditCardFromState,
  selectPutCreditCardAccountCausedErrorFromState,
  selectPutCreditCardBillingCausedErrorFromState,
  selectPutCreditCardErrorDetailMapFromState,
  selectPutCreditCardTopLevelErrorFromState,
  selectPuttingCreditCardAccountFromState,
  selectPuttingCreditCardBillingFromState,
  selectPuttingCreditCardFromState,
  selectReplaceCreditCardErrorDetailMapFromState,
  selectReplaceCreditCardTopLevelErrorFromState,
} from 'src/app/AccountSettings/redux/accountSettings.selectors'
import { createSetIsToastOpen } from 'src/app/Toast/redux/toast.actions'
import AccountBillingInformationBox, {
  AccountBillingInformationCountryFields,
  AccountBillingInformationFlatFields,
} from 'src/components/AccountBillingInformationBox/AccountBillingInformationBox'
import AccountInformationBox, {
  AccountInformationFlatFields,
} from 'src/components/AccountInformationBox/AccountInformationBox'
import AccountVerificationBlockedBox from 'src/components/AccountVerificationBlockedBox/AccountVerificationBlockedBox'
import AccountVerificationRequiredBox from 'src/components/AccountVerificationRequiredBox/AccountVerificationRequiredBox'
import BackgroundPageContent from 'src/components/BackgroundPageContent/BackgroundPageContent'
import CardInformationBox, {
  CardInformationBoxFlatFields,
} from 'src/components/CardInformationBox/CardInformationBox'
import HorizontalContentCollection from 'src/components/HorizontalContentCollection/HorizontalContentCollection'
import NotFoundBackgroundPageContent from 'src/components/NotFoundBackgroundPageContent/NotFoundBackgroundPageContent'
import ResourceCenterLink from 'src/components/ResourceCenterLink/ResourceCenterLink'
import TestId from 'src/components/TestId/TestId'
import VerticalContentCollection from 'src/components/VerticalContentCollection/VerticalContentCollection'
import { useCurrentUser } from 'src/context/UserProvider/useUser'
import { SupportedBrands } from 'src/enums'
import PhysicalCardTransactionsPanel from 'src/feature/PhysicalCardTransactionsPanel/PhysicalCardTransactionsPanel'
import UpdatedPhysicalCardSettings, {
  UpdatedBillingDetails,
} from 'src/feature/UpdatedPhysicalCardSettings/UpdatedPhysicalCardSettings'
import VirtualCardCapabilitiesPanel from 'src/feature/VirtualCardCapabilitiesPanel/VirtualCardCapabilitiesPanel'
import { useBackgroundDispatch, usePrevious } from 'src/hooks'
import { useTranslate } from 'src/localization/Trns'
import {
  PhysicalCardStatus,
  PhysicalCardStatusReason,
} from 'src/models/creditCard.models'
import { UserFeature } from 'src/models/user.models'
import { useQueryUserFeatures } from 'src/queries/auth.queries'
import {
  countriesQueries,
  toCountryOptions,
} from 'src/queries/countries/countries.queries'
import {
  useQueryCreditCard,
  useQueryCreditCardPermissions,
} from 'src/queries/creditCard.queries'
import {
  useStartCreditCardVerification,
  useUpdateCreditCard,
  useVerifyCreditCard,
} from 'src/queries/creditCards/creditCardWrites.queries'
import { creditCardsQueryKeys } from 'src/queries/queries.constants'
import {
  PUT_CREDIT_CARD_ACCOUNT_INIT,
  PUT_CREDIT_CARD_BILLING_INIT,
  putCreditCard,
  replaceCreditCard,
} from 'src/redux/actions/data/creditCards.actions'
import { selectNullSafeLocaleFromState } from 'src/redux/selectors/auth.selectors'
import { selectFeatureFlaggerFromState } from 'src/redux/selectors/data/featureFlagger.selectors'
import { isUpdatedPhysicalCardSettingsEnabled } from 'src/utils/featureFlags'
import { useWhitelabel } from 'src/utils/whitelabel.utils'
import { css } from 'styled-components'

const AccountSettings: React.FC = () => {
  const translate = useTranslate()

  const creditCardId = useSelector(selectCreditCardIdFromRoute)
  const { data: creditCard, refetch: refetchCreditCard } = useQueryCreditCard({
    creditCardId: creditCardId,
    enabled: !!creditCardId,
  })
  const { data: creditCardPermissions } = useQueryCreditCardPermissions({
    creditCardId: creditCard?.id,
    enabled: !!creditCard?.id,
    retry: false,
  })

  const { data: userFeatures } = useQueryUserFeatures([
    UserFeature.PHYSICAL_CARD_TRANSACTIONS_FEED,
  ])
  const physicalCardTransactionsFeedEnabled =
    userFeatures?.[UserFeature.PHYSICAL_CARD_TRANSACTIONS_FEED] === true
  const physicalCardStatus = creditCard?.physicalCard?.status
  const hasPhysicalCard = !!physicalCardStatus
  const isPhysicalCardActive = physicalCardStatus === PhysicalCardStatus.Active

  // Gate the panel on the user feature, with an escape hatch (Active card →
  // still show, but only to allow disconnect).
  const showPhysicalCardTransactionsPanel =
    hasPhysicalCard &&
    (physicalCardTransactionsFeedEnabled || isPhysicalCardActive)
  const canConnect = physicalCardTransactionsFeedEnabled

  const verifyMutation = useVerifyCreditCard()
  const startVerificationMutation = useStartCreditCardVerification()
  const updateCreditCardMutation = useUpdateCreditCard()

  const { whiteLabelIncludes } = useWhitelabel()

  const isExtendBrand = whiteLabelIncludes(SupportedBrands.Extend)

  const remoteConfig = useSelector(selectFeatureFlaggerFromState)
  // EX-40354 — the redesigned bento is a *physical card* surface (freeze,
  // report lost/stolen, replace card, physical card feed), so it needs the same
  // card-type gate the legacy CardInformationBox uses (`!isVirtualAccount`)
  // plus an actual physical card. On a virtual-only account those rows are
  // meaningless, so fall through to the legacy layout.
  const showUpdatedPhysicalCardSettings =
    isUpdatedPhysicalCardSettingsEnabled(remoteConfig) &&
    hasPhysicalCard &&
    !creditCard?.partner?.isVirtualAccount

  // The redesigned tiles are props-only (src/AGENTS.md layering), so the page
  // resolves the reference data, gates and side effects they need.
  const locale = useSelector(selectNullSafeLocaleFromState)
  const { data: countryOptions = [] } = useQuery({
    ...countriesQueries.list(locale),
    select: toCountryOptions,
    enabled: showUpdatedPhysicalCardSettings && Boolean(locale),
  })

  // The card carries no phone field; the design's "Mobile phone" row shows the
  // signed-in user's real number, read-only. `undefined` → the row is omitted.
  const contactPhone = useCurrentUser()?.phone

  const queryClient = useQueryClient()
  const toastDispatch = useDispatch()

  // Keep creditCard.physicalCard.status in sync after a capability change so
  // visibility/state recompute on the latest server state.
  const invalidateCreditCardDetail = useCallback((): void => {
    if (creditCard?.id === undefined) return
    queryClient.invalidateQueries({
      queryKey: creditCardsQueryKeys.detail(creditCard.id),
      exact: true,
    })
  }, [creditCard?.id, queryClient])

  const onPhysicalCardFeedConnected = useCallback((): void => {
    toastDispatch(
      createSetIsToastOpen(true, {
        toastMessage: <p>{translate('Success! Your card is now active.')}</p>,
      }),
    )
    invalidateCreditCardDetail()
  }, [toastDispatch, translate, invalidateCreditCardDetail])

  const puttingCreditCard = useSelector(selectPuttingCreditCardFromState)
  const puttingCreditCardAccount = useSelector(
    selectPuttingCreditCardAccountFromState,
  )
  const puttingCreditCardBilling = useSelector(
    selectPuttingCreditCardBillingFromState,
  )
  const putCreditCardAccountCausedError = useSelector(
    selectPutCreditCardAccountCausedErrorFromState,
  )
  const putCreditCardBillingCausedError = useSelector(
    selectPutCreditCardBillingCausedErrorFromState,
  )
  const patchingCreditCard = useSelector(selectPatchingCreditCardFromState)
  const putCreditCardTopLevelError = useSelector(
    selectPutCreditCardTopLevelErrorFromState,
  )
  const putCreditCardErrorDetailMap = useSelector(
    selectPutCreditCardErrorDetailMapFromState,
  )
  const replaceCreditCardTopLevelError = useSelector(
    selectReplaceCreditCardTopLevelErrorFromState,
  )
  const replaceCreditCardErrorDetailMap = useSelector(
    selectReplaceCreditCardErrorDetailMapFromState,
  )
  const canUpdateCreditCard = !!creditCardPermissions?.canUpdateCreditCard

  const dispatch = useBackgroundDispatch()

  const prevPuttingAccount = usePrevious(puttingCreditCardAccount)
  const prevPuttingBilling = usePrevious(puttingCreditCardBilling)
  const prevPuttingAny = usePrevious(puttingCreditCard)

  const refreshCard = useCallback(() => {
    refetchCreditCard()
  }, [creditCardId /*, refetch */])

  useEffect(() => {
    const finished = prevPuttingAccount && !puttingCreditCardAccount
    const succeeded = finished && !putCreditCardAccountCausedError
    if (succeeded) refreshCard()
  }, [
    prevPuttingAccount,
    puttingCreditCardAccount,
    putCreditCardAccountCausedError,
    refreshCard,
  ])

  useEffect(() => {
    const finished = prevPuttingBilling && !puttingCreditCardBilling
    const succeeded = finished && !putCreditCardBillingCausedError
    if (succeeded) refreshCard()
  }, [
    prevPuttingBilling,
    puttingCreditCardBilling,
    putCreditCardBillingCausedError,
    refreshCard,
  ])

  useEffect(() => {
    const finished = prevPuttingAny && !puttingCreditCard
    const noTopLevelError =
      !replaceCreditCardTopLevelError &&
      Object.keys(replaceCreditCardErrorDetailMap ?? {}).length === 0

    if (finished && noTopLevelError) {
      refreshCard()
    }
  }, [
    prevPuttingAny,
    puttingCreditCard,
    replaceCreditCardTopLevelError,
    replaceCreditCardErrorDetailMap,
    refreshCard,
  ])

  const onBillingInformationSuccessfulSubmit = (
    flatFields: AccountBillingInformationFlatFields,
    countryFields: AccountBillingInformationCountryFields,
  ): void => {
    if (creditCard !== undefined) {
      dispatch(
        putCreditCard(
          creditCard.id,
          {
            ...creditCard,
            ...flatFields,
            ...countryFields,
          },
          false,
          {
            type: PUT_CREDIT_CARD_BILLING_INIT,
          },
        ),
      )
    }
  }

  const onAccountInformationSuccessfulSubmit = (
    flatFields: AccountInformationFlatFields,
  ): void => {
    if (creditCard !== undefined) {
      dispatch(
        putCreditCard(
          creditCard.id,
          {
            ...creditCard,
            ...flatFields,
            ...creditCard.address,
          },
          false,
          {
            type: PUT_CREDIT_CARD_ACCOUNT_INIT,
          },
        ),
      )
    }
  }

  // EX-40354 — the redesigned Card settings tile edits card name + billing
  // address in one form; persist via the same PUT /creditcards/:id used by the
  // legacy billing box (billing init type so its saving/error flags drive the
  // tile's loading state and the existing refetch effect fires on success).
  // EX-40354 — the redesigned Card settings tile is NEW server-state code, so
  // its write goes through TanStack Query (`useUpdateCreditCard`, which
  // invalidates the card's detail key in onSuccess) rather than the legacy
  // `putCreditCard` thunk + effect-driven refetch the older boxes still use.
  const onUpdatedPhysicalCardBillingSave = (
    details: UpdatedBillingDetails,
  ): void => {
    if (creditCard !== undefined) {
      updateCreditCardMutation.mutate({
        creditCardId: creditCard.id,
        body: {
          ...creditCard,
          displayName: details.displayName,
          companyName: details.companyName,
          country: details.country,
          address1: details.address1,
          address2: details.address2,
          city: details.city,
          province: details.province,
          postal: details.postal,
        },
      })
    }
  }

  const onCardInformationSuccessfulSubmit = (
    flatFields: CardInformationBoxFlatFields,
  ): void => {
    if (creditCard !== undefined) {
      dispatch(
        replaceCreditCard(creditCard.id, {
          ...flatFields,
        }),
      )
    }
  }

  // TODO: When physicalCard.status=BLOCKED but status=ACTIVE, we show the
  // blocked box and hide all account settings. Decide if billing/account info
  // should still be accessible since the block only affects physical card
  // verification, not the account itself.
  const showBlocked = creditCard?.isBlocked
  const showCVSVerificationFailed =
    !showBlocked &&
    creditCard?.physicalCard.status === PhysicalCardStatus.NotApplicable &&
    creditCard?.physicalCard.statusReason ===
      PhysicalCardStatusReason.CvsVerificationFailed
  const showVerificationRequired =
    !showBlocked &&
    !showCVSVerificationFailed &&
    creditCard?.physicalCard.status === PhysicalCardStatus.NeedsCVSVerification

  return creditCard !== undefined ? (
    <BackgroundPageContent>
      {showBlocked ? (
        <AccountVerificationBlockedBox
          secondParagraph={translate(
            'There have been too many failed attempts at verifying the account. In order to proceed, contact us via our ${resourceCenter}.',
            {
              resourceCenter: (
                <ResourceCenterLink
                  underline
                  css={css`
                    font-family: ${styleConstants.fontFamilies.regular};
                    font-size: 16px;
                    &:hover {
                      font-family: ${styleConstants.fontFamilies.regular};
                      font-size: 16px;
                    }
                  `}
                />
              ),
            },
          )}
        />
      ) : showCVSVerificationFailed ? (
        <AccountVerificationBlockedBox
          secondParagraph={translate(
            'Please check your card information and try enrolling again, or contact us via our ${resourceCenter}.',
            {
              resourceCenter: (
                <ResourceCenterLink
                  underline
                  css={css`
                    font-family: ${styleConstants.fontFamilies.regular};
                    font-size: 16px;
                    &:hover {
                      font-family: ${styleConstants.fontFamilies.regular};
                      font-size: 16px;
                    }
                  `}
                />
              ),
            },
          )}
        />
      ) : showVerificationRequired ? (
        <AccountVerificationRequiredBox
          accountResendVerificationError={
            startVerificationMutation.error?.error
          }
          accountVerificationError={verifyMutation.error?.error}
          canVerifyAgain={creditCard.canVerifyAgain}
          handleResendVerification={() =>
            startVerificationMutation.mutate({ creditCardId })
          }
          handleVerifyCreditCard={(code) =>
            verifyMutation.mutate({ creditCardId, body: { code } })
          }
          postingAccountVerification={verifyMutation.isPending}
          postingResendAccountVerification={startVerificationMutation.isPending}
          timeUntilNextVerificationRequested={
            creditCard.timeUntilNextVerificationRequested
          }
          canUpdateCreditCard={canUpdateCreditCard}
        />
      ) : showUpdatedPhysicalCardSettings ? (
        <UpdatedPhysicalCardSettings
          creditCard={creditCard}
          countryOptions={countryOptions}
          contactPhone={contactPhone}
          // Same permission the legacy boxes gate editing on — without it the
          // tile must not offer a Save that PUT /creditcards/:id would reject.
          canEditCardDetails={canUpdateCreditCard}
          // The physical-feed switch opens the real enroll flow, so it carries
          // the same PHYSICAL_CARD_TRANSACTIONS_FEED entitlement gate
          // PhysicalCardTransactionsPanel applies to its Connect CTA.
          canConnectPhysicalCardFeed={canConnect}
          // Both panels this disclosure replaces are Extend-brand only.
          showConnectedCapabilities={isExtendBrand}
          onSaveBillingDetails={onUpdatedPhysicalCardBillingSave}
          isSavingBillingDetails={updateCreditCardMutation.isPending}
          billingDetailsSaveError={updateCreditCardMutation.isError}
          onPhysicalCardFeedConnected={onPhysicalCardFeedConnected}
          onPhysicalCardFeedDisconnected={invalidateCreditCardDetail}
        />
      ) : (
        <HorizontalContentCollection>
          <VerticalContentCollection>
            <AccountInformationBox
              creditCard={creditCard}
              topLevelError={putCreditCardTopLevelError}
              puttingCreditCard={puttingCreditCard}
              puttingCreditCardAccount={puttingCreditCardAccount}
              onSuccessfulSubmit={onAccountInformationSuccessfulSubmit}
              hasEditPermissions={canUpdateCreditCard}
              inFocus={putCreditCardAccountCausedError}
            />
            <AccountBillingInformationBox
              creditCard={creditCard}
              puttingCreditCard={puttingCreditCard}
              puttingCreditCardBilling={puttingCreditCardBilling}
              topLevelError={putCreditCardTopLevelError}
              errorDetailMap={putCreditCardErrorDetailMap}
              onSuccessfulSubmit={onBillingInformationSuccessfulSubmit}
              hasEditPermissions={canUpdateCreditCard}
              inFocus={putCreditCardBillingCausedError}
            />
          </VerticalContentCollection>
          <VerticalContentCollection>
            {!creditCard.partner?.isVirtualAccount && (
              <TestId testId={'cardInformationBox'}>
                <CardInformationBox
                  creditCard={creditCard}
                  puttingCreditCard={puttingCreditCard}
                  patchingCreditCard={patchingCreditCard}
                  topLevelError={replaceCreditCardTopLevelError}
                  errorDetailMap={replaceCreditCardErrorDetailMap}
                  onSuccessfulSubmit={onCardInformationSuccessfulSubmit}
                  hasEditPermissions={
                    creditCard.features.rcnReplacementEnabled &&
                    canUpdateCreditCard
                  }
                />
              </TestId>
            )}
            {isExtendBrand && showPhysicalCardTransactionsPanel && (
              <PhysicalCardTransactionsPanel
                creditCard={creditCard}
                canConnect={canConnect}
              />
            )}
            {isExtendBrand && (
              <VirtualCardCapabilitiesPanel creditCard={creditCard} />
            )}
          </VerticalContentCollection>
        </HorizontalContentCollection>
      )}
    </BackgroundPageContent>
  ) : (
    <NotFoundBackgroundPageContent />
  )
}

export default AccountSettings
